import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Users } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../components/private/Breadcrumbs";
import api from "../../api";
import { useUser, useUserRoles } from "../../authentication/authentication";
import { apiRequest, apiSuccess } from "../../redux/api/apiActions";
import { showError } from "../../redux/error/errorActions";
import {
  clearConfirmationAlerts,
  hideAlert,
  showAlert,
} from "../../redux/alert/alertActions";
import { checkAccess } from "../../utils/usePageAccess";
import errorChecker from "../../utils/errorChecker";
import { isAdminRole } from "../../utils/adminChecker";
import { useNavigate } from "react-router-dom";
import { arClass } from "../../utils/useArClass";
import textTranslator from "../../utils/useTextTranslator";
import Txt from "../../components/Text";
import WaitingRoomStats from "./components/WaitingRoomStats";
import WaitingRoomHeader from "./components/WaitingRoomHeader";
import WaitingRoomFilters from "./components/WaitingRoomFilters";
import WaitingRoomControls from "./components/WaitingRoomControls";
import GridView from "./components/GridView";
import TableView from "./components/TableView";

const WAITING_LIST_KEY = "waiting-room-list";
const CREATE_WAITING_KEY = "waiting-room-create";

/**
 * Text Component Wrapper for Waiting Room Page
 * Props:
 * - children: Text content
 * - className: Additional CSS classes
 * @returns Text JSX
 */
const Text = ({ children, className, page = "WAITING_ROOM" }) => {
  return (
    <Txt page={page} className={className}>
      {children}
    </Txt>
  );
};

/**
 * Waiting Room Main Component
 * @returns Waiting Room JSX
 */
const getTheme = (darkMode) => ({
  card: darkMode
    ? "bg-slate-800 border-slate-700 text-white"
    : "bg-white border-slate-200 text-slate-800",
  tableHeader: darkMode ? "text-slate-300" : "text-slate-600",
  tableRow: darkMode ? "border-slate-700" : "border-slate-200",
  background: darkMode
    ? "bg-slate-700 border-slate-600"
    : "bg-slate-50 border-slate-200",
  input: darkMode
    ? "bg-slate-700 border-slate-600 text-white"
    : "bg-white border-gray-200 text-slate-800",
  dropdown: darkMode ? "" : "",
  dropdownHover: darkMode ? "" : "",
  textPrimary: darkMode ? "text-slate-200" : "text-slate-800",
  textSecondary: darkMode ? "text-slate-400" : "text-slate-500",
  border: darkMode ? "border-slate-700" : "border-slate-200",
  hover: darkMode ? "hover:bg-slate-700" : "hover:bg-slate-50",
  skeleton: darkMode ? "bg-slate-700" : "bg-slate-200",
});

/**
 * Initial filter state
 * @returns Initial filter object
 */
const initialFilters = {
  status: "today",
  sort: "position-asc",
};

const getStuts = (status, darkMode, defaultLanguage) => {
  const colors = {
    waiting: darkMode
      ? "bg-blue-800 text-blue-100"
      : "bg-blue-100 text-blue-800",
    with_nurse: darkMode
      ? "bg-violet-800 text-violet-100"
      : "bg-violet-100 text-violet-800",
    with_doctor: darkMode
      ? "bg-emerald-800 text-emerald-100"
      : "bg-emerald-100 text-emerald-800",
    done: darkMode
      ? "bg-teal-800 text-teal-100"
      : "bg-teal-100 text-teal-800",
    cancelled: darkMode
      ? "bg-rose-800 text-rose-100"
      : "bg-rose-100 text-rose-800",
    absent: darkMode
      ? "bg-amber-800 text-amber-100"
      : "bg-amber-100 text-amber-800",
    payment: darkMode
      ? "bg-yellow-800 text-yellow-100"
      : "bg-yellow-100 text-yellow-800",
  };
  const borderStyles = {
    waiting: darkMode
      ? "border-l-[6px] border-l-blue-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-blue-500 max-sm:border-l-[3px]",
    with_nurse: darkMode
      ? "border-l-[6px] border-l-violet-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-violet-500 max-sm:border-l-[3px]",
    with_doctor: darkMode
      ? "border-l-[6px] border-l-emerald-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-emerald-500 max-sm:border-l-[3px]",
    done: darkMode
      ? "border-l-[6px] border-l-teal-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-teal-500 max-sm:border-l-[3px]",
    cancelled: darkMode
      ? "border-l-[6px] border-l-rose-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-rose-500 max-sm:border-l-[3px]",
    absent: darkMode
      ? "border-l-[6px] border-l-amber-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-amber-500 max-sm:border-l-[3px]",
    payment: darkMode
      ? "border-l-[6px] border-l-yellow-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-yellow-500 max-sm:border-l-[3px]",
  };

  const borderStylesRtl = {
    waiting: darkMode
      ? "border-r-[6px] border-r-blue-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-blue-500 max-sm:border-r-[3px]",
    with_nurse: darkMode
      ? "border-r-[6px] border-r-violet-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-violet-500 max-sm:border-r-[3px]",
    with_doctor: darkMode
      ? "border-r-[6px] border-r-emerald-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-emerald-500 max-sm:border-r-[3px]",
    done: darkMode
      ? "border-r-[6px] border-r-teal-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-teal-500 max-sm:border-r-[3px]",
    cancelled: darkMode
      ? "border-r-[6px] border-r-rose-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-rose-500 max-sm:border-r-[3px]",
    absent: darkMode
      ? "border-r-[6px] border-r-amber-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-amber-500 max-sm:border-r-[3px]",
    payment: darkMode
      ? "border-r-[6px] border-r-yellow-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-yellow-500 max-sm:border-r-[3px]",
  };

  if (defaultLanguage === "ar") {
    return {
      status,
      color:
        colors[status] ||
        (darkMode
          ? "bg-slate-800 text-slate-100"
          : "bg-slate-100 text-slate-800"),
      border:
        borderStylesRtl[status] ||
        (darkMode
          ? "border-r-[6px] border-r-slate-700 max-sm:border-r-[3px]"
          : "border-r-[6px] border-r-slate-300 max-sm:border-r-[3px]"),
    };
  } else {
    return {
      status,
      color:
        colors[status] ||
        (darkMode
          ? "bg-slate-800 text-slate-100"
          : "bg-slate-100 text-slate-800"),
      border:
        borderStyles[status] ||
        (darkMode
          ? "border-l-[6px] border-l-slate-700 max-sm:border-l-[3px]"
          : "border-l-[6px] border-l-slate-300 max-sm:border-l-[3px]"),
    };
  }
};
/**
 * Badge class mappings
 * @returns Badge class object
 */
const statusBadgeClass = (darkMode, status) => {
  const defaultClasses = {
    waiting: darkMode
      ? "bg-blue-800 !text-blue-100 border border-blue-700"
      : "bg-blue-100 !text-blue-800 border border-blue-300",
    with_nurse: darkMode
      ? "bg-violet-800 !text-violet-100 border border-violet-700"
      : "bg-violet-100 !text-violet-800 border border-violet-300",
    with_doctor: darkMode
      ? "bg-emerald-800 !text-emerald-100 border border-emerald-700"
      : "bg-emerald-100 !text-emerald-800 border border-emerald-300",
    done: darkMode
      ? "bg-teal-800 !text-teal-100 border border-teal-700"
      : "bg-teal-100 !text-teal-800 border border-teal-300",
    cancelled: darkMode
      ? "bg-rose-800 !text-rose-100 border border-rose-700"
      : "bg-rose-100 !text-rose-800 border border-rose-300",
    absent: darkMode
      ? "bg-amber-800 !text-amber-100 border border-amber-700"
      : "bg-amber-100 !text-amber-800 border border-amber-300",
    payment: darkMode
      ? "bg-yellow-800 !text-yellow-100 border border-yellow-700"
      : "bg-yellow-100 !text-yellow-800 border border-yellow-300",
  };
  return (
    defaultClasses[status] ||
    (darkMode
      ? "bg-slate-800 !text-slate-100 border border-slate-700"
      : "bg-slate-100 !text-slate-800 border border-slate-300")
  );
};

/**
 * Badge class mappings for urgency levels
 * @returns Urgency badge class object
 */
const urgencyBadgeClass = (darkMode, urgency) => {
  const defaultClasses = {
    High: darkMode
      ? "bg-red-800 text-red-100 border border-red-700"
      : "bg-red-100 text-red-800 border border-red-300",
    Medium: darkMode
      ? "bg-yellow-800 !text-yellow-100 border border-yellow-700"
      : "bg-yellow-100 text-yellow-600 border border-yellow-200",
    Low: darkMode
      ? "bg-green-800 !text-green-100 border border-green-700"
      : "bg-green-100 text-green-600 border border-green-200",
  };
  return (
    defaultClasses[urgency] ||
    "bg-slate-100 text-slate-800 border border-slate-300"
  );
};
/**
 * Compute elapsed minutes since entry creation or patient entered
 * @returns Elapsed minutes number
 */
const computeElapsedMinutes = (entry) => {
  const base = entry?.enteredAt || entry?.createdAt;
  if (!base) return 0;
  const diff = Date.now() - new Date(base).getTime();
  return Math.max(0, Math.floor(diff / 60000));
};

/**
 * Get waiting time in HH:MM:SS (UTC-safe)
 * @returns Waiting time string
 */
function getWaitingTime(entry, now = Date.now()) {
  if (!entry?.enteredAt) return "00:00:00";

  const start = Date.parse(entry.enteredAt); // UTC
  const end = entry.leftAt
    ? Date.parse(entry.leftAt) // UTC if exists
    : now; // live timestamp

  if (end < start) return "00:00:00";

  const totalSeconds = Math.floor((end - start) / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return (
    String(hours).padStart(2, "0") +
    ":" +
    String(minutes).padStart(2, "0") +
    ":" +
    String(seconds).padStart(2, "0")
  );
}

/**
 * Get average waiting time from entries
 * @returns Average waiting time string
 */
function getAvgWaitingTime(entries, now = Date.now()) {
  if (!Array.isArray(entries) || entries.length === 0) return "00:00";

  let totalSeconds = 0;
  let count = 0;

  for (const entry of entries) {
    if (!entry.enteredAt) continue;

    const start = Date.parse(entry.enteredAt);
    const end = entry.leftAt ? Date.parse(entry.leftAt) : now;

    const seconds = Math.max(0, Math.floor((end - start) / 1000));

    if (seconds <= 0) continue;

    totalSeconds += seconds;
    count++;
  }

  if (count === 0) return "00:00";

  const avgSeconds = Math.floor(totalSeconds / count);
  const h = Math.floor(avgSeconds / 3600);
  const m = Math.floor((avgSeconds % 3600) / 60);

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
/**
 * Waiting Room Main Component
 * @returns Waiting Room JSX
 */
const WaitingRoom = () => {
  const deleteSelectedEntriesKey = "delete-selected-waiting-room-entries";
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const navigate = useNavigate();
  const { requests } = useSelector((state) => state.api);
  const newWaitingRequest = requests[CREATE_WAITING_KEY]?.success || false;
  const deleteSelectedRequest =
    requests[deleteSelectedEntriesKey]?.loading || false;
  const deleteSelectedSuccess =
    requests[deleteSelectedEntriesKey]?.success || false;
  const dispatch = useDispatch();
  const darkMode = useSelector((state) => state?.settings?.darkMode);
  const receiveData = useSelector((state) => state.realTimeData);

  const user = useUser();
  const theme = getTheme(darkMode);
  const userId = user?.id || null;
  const [rawEntries, setRawEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [actionsOpenId, setActionsOpenId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null); // store clicked button DOM node
  const [actionLoading, setActionLoading] = useState({ id: null, type: null });
  const [now, setNow] = useState(Date.now());
  const [showActionDescription, setShowActionDescription] = useState({
    id: null,
    type: null,
  });
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const roles = useUserRoles();
  const canDeleteEntries = checkAccess("deleteWaitingRoom");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'table'
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const avgWaitingTime = useMemo(
    () => getAvgWaitingTime(rawEntries, now),
    [rawEntries, now],
  );

  // fetch entries from backend using filters & search (server-side)
  const fetchWaitingEntries = useCallback(
    async (opts = {}) => {
      if (!userId) return;
      try {
        setLoading(true);
        dispatch(apiRequest("GET", WAITING_LIST_KEY));
        const isToday = filters.status === "today";
        const params = {
          page: 1,
          pageSize: 200,
          sort: filters.sort,
          q: searchTerm || undefined,
          status:
            filters.status && filters.status !== "all" && !isToday
              ? filters.status
              : undefined,
          date: isToday
            ? new Date(
                new Date().getTime() - new Date().getTimezoneOffset() * 60000,
              )
                .toISOString()
                .split("T")[0]
            : undefined,
          ...opts,
        };
        const res = await api.get(`waiting-rooms/doctor/${userId}`, {
          params,
        });
        const data = res?.data?.results ?? res?.data?.data ?? [];
        setRawEntries(Array.isArray(data) ? data : []);
        dispatch(apiSuccess("GET", WAITING_LIST_KEY, "Waiting room loaded"));
      } catch (error) {
        dispatch(
          showError(
            "waitingRoomFetch",
            error?.response?.data?.error ||
              "Unable to load waiting room entries.",
          ),
        );
      } finally {
        setLoading(false);
      }
    },
    [userId, dispatch, filters.sort, filters.status, searchTerm],
  );
// call server on mount and whenever filters/search change
  useEffect(() => {
    fetchWaitingEntries();
  }, [fetchWaitingEntries]);

  useEffect(() => {
    if (newWaitingRequest || deleteSelectedSuccess) {
      fetchWaitingEntries();
    }
  }, [newWaitingRequest, deleteSelectedSuccess, fetchWaitingEntries]);

  // listen to real-time updates
  useEffect(() => {
    if (
      receiveData.dataModel === "waitingroom" &&
      receiveData.methodType === "post"
    ) {
      const updatedEntry = receiveData.data?.waitingRoom;
      if (updatedEntry) {
        setRawEntries((prev) => {
          const exists = prev.find((item) => item.id === updatedEntry.id);
          if (exists) {
            // update existing entry
            return prev.map((item) =>
              item.id === updatedEntry.id ? updatedEntry : item,
            );
          } else {
            // new entry added
            return [updatedEntry, ...prev];
          }
        });
      }
    } else if (
      (receiveData.dataModel === "visit" ||
        receiveData.dataModel === "waitingroom") &&
      (receiveData.methodType === "patch" || receiveData.methodType === "put")
    ) {
      if (receiveData.dataModel === "visit") {
        const visitData = receiveData.data?.visit || receiveData.data;
        const waitingRoomData =
          receiveData.data?.waitingRoom || visitData?.waitingRoom || null;

        if (waitingRoomData) {
          // Update matching waiting room entry and refresh nested visit
          setRawEntries((prev) =>
            prev.map((item) => {
              if (item.id === waitingRoomData.id) {
                return {
                  ...item,
                  ...waitingRoomData,
                  visit: visitData,
                };
              }
              return item;
            }),
          );
        } else {
          // Fallback when waitingRoom is not included: match by visit id only.
          setRawEntries((prev) =>
            prev.map((item) => {
              if (item.visit?.id === visitData?.id) {
                return {
                  ...item,
                  visit: visitData,
                };
              }
              return item;
            }),
          );
        }
      } else {
        // For waitingroom updates, replace the entire entry
        const updatedEntry = receiveData.data;
        setRawEntries((prev) =>
          prev.map((item) =>
            item.id === updatedEntry.id ? updatedEntry : item,
          ),
        );
      }
    } else if (
      (receiveData.dataModel === "visit" ||
        receiveData.dataModel === "waitingroom") &&
      receiveData.methodType === "delete"
    ) {
      if (receiveData.data.all) {
        const deletedIds = receiveData.data.deletedIds;
        setRawEntries((prev) =>
          prev.filter((item) => !deletedIds.includes(item.id)),
        );
      } else {
        const deletedId = receiveData.data.id;
        setRawEntries((prev) => prev.filter((item) => item.id !== deletedId));
      }
    }
  }, [receiveData]);
// server-side filtering -> do not filter client-side
  // keep rawEntries as the source of truth
  const stats = useMemo(() => {
    const waiting = rawEntries.filter(
      (entry) => entry.status === "waiting",
    ).length;
    const inProgress = rawEntries.filter(
      (entry) => entry.status === "with_doctor",
    ).length;
    const avgWait =
      rawEntries.length === 0
        ? 0
        : Math.round(
            rawEntries.reduce(
              (sum, entry) => sum + computeElapsedMinutes(entry),
              0,
            ) / rawEntries.length,
          );
    return { waiting, inProgress, avgWait };
  }, [rawEntries]);

  const toggleActions = (entryId, buttonEl) => {
    if (actionsOpenId === entryId) {
      setActionsOpenId(null);
      setAnchorEl(null);
    } else {
      setActionsOpenId(entryId);
      setAnchorEl(buttonEl); // store DOM node
    }
  };

  const handleStatusChange = async (entry, visitId, nextStatus) => {
    try {
      switch (nextStatus) {
        case "with_nurse":
          if (
            entry.status === "with_nurse" ||
            entry.status === "with_doctor" ||
            entry.status === "done"
          ) {
            setActionLoading({ id: entry.id, type: null });
            return navigate(`/triage/${visitId}`);
          }
          setActionLoading({ id: entry.id, type: "with_nurse" });
          break;
        case "waiting":
          setActionLoading({ id: entry.id, type: "waiting" });
          break;
        case "with_doctor":
          if (entry.status === "with_doctor" || entry.status === "done") {
            setActionLoading({ id: entry.id, type: null });
            return navigate(`/visit-medical/${visitId}`);
          }
          setActionLoading({ id: entry.id, type: "with_doctor" });
          break;
        case "absent":
          setActionLoading({ id: entry.id, type: "absent" });
          break;

        default:
          setActionLoading({ id: entry.id, type: null });
      }
      dispatch(apiRequest("PATCH", `waiting-room-update-${entry.id}`));

      const res = await api.put(
        `waiting-rooms/update-waiting-room/${entry.id}`,
        { status: nextStatus },
      );

      if (
        nextStatus === "with_nurse" &&
        (roles.includes("nurse") || isAdminRole(roles))
      ) {
        return navigate(`/triage/${visitId}`);
      } else if (nextStatus === "with_doctor" && roles.includes("doctor")) {
        return navigate(`/visit-medical/${visitId}`);
      }
      const updated = res?.data?.data;
      if (updated) {
        // update local list or refetch
        setRawEntries((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
      }
      dispatch(
        apiSuccess(
          "PATCH",
          `waiting-room-update-${entry.id}`,
          textTranslator("Status updated", "WAITING_ROOM"),
        ),
      );
      setActionLoading({ id: null, type: null });
    } catch (error) {
      setActionLoading({ id: null, type: null });
      dispatch(
        showError(
          "waitingRoomUpdate",
          error?.response?.data?.error || "Unable to update patient status.",
        ),
      );
    }
  };
const handleDeleteEntry = async (entry) => {
    try {
      setActionLoading({ id: entry.id, type: "delete" });
      dispatch(apiRequest("DELETE", `waiting-room-delete-${entry.id}`));
      await api.delete(`waiting-rooms/delete-waiting-room/${entry.id}`);
      setRawEntries((prev) => prev.filter((item) => item.id !== entry.id));
      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Entry Deleted", "WAITING_ROOM"),
          message: textTranslator(
            "The waiting room entry has been successfully deleted.",
            "WAITING_ROOM",
          ),
        }),
      );
      dispatch(
        apiSuccess(
          "DELETE",
          `waiting-room-delete-${entry.id}`,
          textTranslator("Entry deleted", "WAITING_ROOM"),
        ),
      );
      setActionLoading({ id: null, type: null });
    } catch (error) {
      setActionLoading({ id: null, type: null });
      dispatch(
        showError(
          "waitingRoomDelete",
          error?.response?.data?.error ||
            "Unable to delete waiting room entry.",
        ),
      );
    }
  };

  const handleDeleteAll = () => {
    dispatch(clearConfirmationAlerts());
    dispatch(
      showAlert({
        type: "confirmation",
        title: textTranslator(
          "Delete Selected Waiting Room Entries",
          "WAITING_ROOM",
        ),
        message: `${textTranslator("Are you sure you want to delete the ", "WAITING_ROOM")} ${selectedIds.length} ${textTranslator(
          "selected entries",
          "WAITING_ROOM",
        )}? ${textTranslator("This action cannot be undone.", "WAITING_ROOM")}`,
        onCancel: cancelDelete,
        onConfirm: confirmDeleteAll,
      }),
    );
  };

  const confirmDeleteAll = () => {
    dispatch(apiRequest("DELETE", deleteSelectedEntriesKey));
    api
      .delete(`waiting-rooms/delete-selected`, {
        data: {
          selectedIds: selectedIds,
        },
      })
      .then((res) => {
        dispatch(
          apiSuccess(
            "DELETE",
            deleteSelectedEntriesKey,
            "Waiting room entries deleted successfully",
          ),
        );

        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("Deleted Successfully", "WAITING_ROOM"),
            message: `${textTranslator("theSelectedWaitingRoomEntries", "WAITING_ROOM")} ${selectedIds.length} ${textTranslator(
              "wereDeletedSuccessfully",
              "WAITING_ROOM",
            )}`,
          }),
        );
        setSelectedIds([]);
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.error ||
          "Erreur lors de la récupération des données.";
        const type = error?.response?.data?.type || null;
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  };

  const cancelDelete = (id) => {
    dispatch(hideAlert(id));
    setSelectedIds([]);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === rawEntries?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rawEntries?.map((r) => r.id) || []);
    }
  };

  const handleSelectItem = (waitingRoomId) => {
    setSelectedIds((prev) =>
      prev.includes(waitingRoomId)
        ? prev.filter((id) => id !== waitingRoomId)
        : [...prev, waitingRoomId],
    );
  };

  const getUrgency = (entry) => {
    const minutes = computeElapsedMinutes(entry);
    if (minutes >= 45) return "High";
    if (minutes >= 20) return "Medium";
    return "Low";
  };

  // status buttons that control server-side filter
  const statusButtons = [
    { key: "all", labelKey: "statusAll" },
    { key: "today", labelKey: "statusToday" },
    { key: "waiting", labelKey: "statusWaiting" },
    { key: "with_nurse", labelKey: "statusWithNurse" },
    { key: "with_doctor", labelKey: "statusWithDoctor" },
    { key: "done", labelKey: "statusDone" },
    { key: "cancelled", labelKey: "statusCancelled" },
    { key: "absent", labelKey: "statusAbsent" },
    { key: "billing", labelKey: "statusPayment" },
  ];

  // pre-compute the fields shared by both the grid and table views
  const patientItems = useMemo(
    () =>
      rawEntries.map((entry) => {
        const minutes = getWaitingTime(entry, now);
        const urgency = getUrgency(entry);
        const sectionState = expandedSections[entry.id] || {
          appointment: true,
          patientDetails: true,
          visitDetails: true,
          payment: true,
        };
        const assignedDoctorName =
          `${entry.assignedDoctor?.firstName || ""} ${
            entry.assignedDoctor?.lastName || ""
          }`.trim() || "-";
        const assignedNurseName =
          `${entry.assignedNurse?.firstName || ""} ${
            entry.assignedNurse?.lastName || ""
          }`.trim() || "-";
        const creatorName =
          `${entry.creator?.firstName || ""} ${
            entry.creator?.lastName || ""
          }`.trim() || "-";
        const paymentInfo = entry.visit?.visitPayment || null;
        const patientName =
          `${entry.patient?.firstName || ""} ${
            entry.patient?.lastName || ""
          }`.trim() || textTranslator("Patient", "WAITING_ROOM");
        return {
          entry,
          minutes,
          urgency,
          sectionState,
          assignedDoctorName,
          assignedNurseName,
          creatorName,
          paymentInfo,
          patientName,
          statusInfo: getStuts(entry.status, darkMode, defaultLanguage),
          statusBadge: statusBadgeClass(darkMode, entry.status),
          urgencyBadge: urgencyBadgeClass(darkMode, urgency),
        };
      }),
    [rawEntries, now, expandedSections, darkMode, defaultLanguage],
  );

  // shared handlers/state passed to both the grid and table views
  const viewProps = {
    setExpandedSections,
    selectedIds,
    handleSelectItem,
    canDeleteEntries,
    toggleActions,
    actionsOpenId,
    anchorEl,
    onCloseActions: () => {
      setActionsOpenId(null);
      setAnchorEl(null);
    },
    actionLoading,
    showActionDescription,
    setShowActionDescription,
    theme,
    roles,
    isAdmin: isAdminRole(roles),
    handleStatusChange,
    handleDeleteEntry,
    navigate,
    darkMode,
    defaultLanguage,
  };

  const emptyState = (
    <div
      className={arClass(
        "col-span-full flex flex-col items-center justify-center py-20 rounded-3xl border-2 border-dashed border-slate-300/60 text-center px-6",
      )}
    >
      <div className="w-16 h-16 rounded-2xl bg-slate-200/60 flex items-center justify-center mb-4">
        <Users className="text-slate-400 max-sm:!text-sm" size={28} />
      </div>
      <h2 className={arClass("text-xl font-semibold mb-2 max-sm:text-sm")}>
        <Text>noPatientsTitle</Text>
      </h2>
      <p className={arClass("text-sm text-slate-500 max-w-md max-sm:text-xs")}>
        <Text>noPatientsDescription</Text>
      </p>
    </div>
  );

  const rootBg = darkMode
    ? "bg-slate-900 text-slate-100"
    : "bg-slate-50 text-slate-900";

  return (
    <div className={arClass(`min-h-screen font-sans pb-24 ${rootBg}`)}>
      <Breadcrumbs />

      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
        <WaitingRoomStats
          stats={stats}
          avgWaitingTime={avgWaitingTime}
          darkMode={darkMode}
          Text={Text}
          textTranslator={textTranslator}
        />

        <WaitingRoomHeader Text={Text} />

        <WaitingRoomFilters
          searchTerm={searchTerm}
          onSearchChange={(value) => setSearchTerm(value)}
          filters={filters}
          onFilterChange={(key, value) =>
            setFilters((prev) => ({ ...prev, [key]: value }))
          }
          onSortChange={(value) =>
            setFilters((prev) => ({ ...prev, sort: value }))
          }
          onClearFilters={() => {
            setFilters(initialFilters);
            setSearchTerm("");
          }}
          darkMode={darkMode}
        />

        <WaitingRoomControls
          statusButtons={statusButtons}
          filters={filters}
          setFilters={setFilters}
          handleSelectAll={handleSelectAll}
          selectedIds={selectedIds}
          deleteSelectedRequest={deleteSelectedRequest}
          handleDeleteAll={handleDeleteAll}
          darkMode={darkMode}
          Text={Text}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          defaultLanguage={defaultLanguage}
        />
        {viewMode === "table" ? (
          <TableView
            items={patientItems}
            viewProps={viewProps}
            loading={loading}
            empty={emptyState}
            darkMode={darkMode}
          />
        ) : (
          <GridView
            items={patientItems}
            viewProps={viewProps}
            loading={loading}
            empty={emptyState}
            darkMode={darkMode}
          />
        )}
      </main>
    </div>
  );
};

export default WaitingRoom;